import { useEffect, useRef, useState } from 'react';

/**
 * Anima un número de su valor previo → value con easing (ease-out cubic) usando rAF.
 * Respeta prefers-reduced-motion (si está activo, salta directo al valor).
 * Devuelve el valor "en curso"; formatéalo tú (money, entero, %, etc.).
 */
export function useCountUp(value: number, duration = 900): number {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef<number>();

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const to = value;
    const from = fromRef.current;
    fromRef.current = to;

    if (reduce || from === to || !Number.isFinite(to)) {
      setDisplay(to);
      return;
    }

    let start: number | null = null;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setDisplay(from + (to - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, duration]);

  return display;
}
